/**
 * TurnTo - Ratings and Reviews
 * Functions that support the TurnTo ratings and reviews
 * implementation. This is meant for small enhancements
 * that help bridge the gap between TurnTo capabilities
 * and UI/UX requirements.
 */

declare global {
  interface Window {
    TurnToCmd: () => void;
    turnToConfig: {
      locale: string | undefined;
      pageId: string;
      sku: string | undefined;
      reviewsList: {
        onUpdate: () => void;
        onFinish: () => void;
      };
    };
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const TurnToCmd: { q: any[] };

let reviewsContainer: HTMLElement | null = null;
let WRITE_A_REVIEW_CTA_TEXT: string | undefined = '';
let WRITE_A_REVIEW_SUBMISSION_FORM: HTMLElement | null = null;

const REVIEWS_HEADING_SELECTOR = '.tt-c-reviews-summary__content .tt-c-reviews-summary__heading';
const NO_REVIEWS_HEADING_SELECTOR =
  '.tt-c-reviews-summary__no-reivews .tt-c-reviews-summary__heading';
const REVIEWS_SUMMARY_SELECTOR =
  '.tt-w-reviews-summary .tt-c-reviews-summary__content .tt-l-grid__row.tt-c-reviews-summary__grid-row:nth-child(2)';
const NO_REVIEWS_SUMMARY_SELECTOR =
  '.tt-c-reviews-summary__no-reivews .tt-c-reviews-summary__no-reivews-body.tt-u-spacing--md';
const WAIT_TIME = 500;
const REVIEWS_WIDGET_HEADER_SELECTOR = '.ratings-and-reviews .ratings-and-reviews-header';
const REVIEWS_SORT_SELECTOR = '.tt-c-reviews-toolbar__sort .tt-o-listbox__btn-label';

export const ttRatingsAndReviews = {
  mounted() {
    reviewsContainer = this.el;
    WRITE_A_REVIEW_CTA_TEXT = document.getElementById('tt-reviews-summary')?.dataset.ctaText;
    WRITE_A_REVIEW_SUBMISSION_FORM = document.getElementById('tt-embedded-submission');

    if (WRITE_A_REVIEW_CTA_TEXT) {
      this.initTurnToConfigs();
    }

    if (WRITE_A_REVIEW_SUBMISSION_FORM) {
      this.initTurnToWriteAReviewConfigs();
    }

    this.loadTurnToScript();
    this.appendWriteReviewCmd(WRITE_A_REVIEW_SUBMISSION_FORM);
  },
  /**
   * Creates an anchor tag in this format:
   * <a href=[url] class="write-review-cta"></a>
   * @param skuParam string
   * @returns HTMLElement
   */
  createCTALink(skuParam: string) {
    const ctaLink = document.createElement('a');
    ctaLink.innerText = WRITE_A_REVIEW_CTA_TEXT ?? '';
    ctaLink.classList.add('write-review-cta');
    ctaLink.setAttribute('href', `/write-a-review?sku=${skuParam}`);
    ctaLink.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('pdp.onReviewStart', { detail: { prodID: skuParam } }));
    });
    return ctaLink;
  },
  /**
   * Inserts a review count total into the TurnTo (TT) Review Summary widget UI
   * The review count format: (X)
   * The review summary widget has 2 states:
   *   1) at least 1 review - e.g. (23)
   *   2) no reviews - e.g. (0)
   * The TT widget will render different HTML elements based on these states. This
   * function determines the Review Summary state and inserts the appropriate text.
   * @param reviewsContainer HTMLElement
   */
  insertReviewTotal() {
    if (!reviewsContainer) {
      return;
    }

    const reviewsText: string = (
      reviewsContainer.querySelector(REVIEWS_HEADING_SELECTOR) as HTMLElement
    )?.innerText;
    const noNoReviewsHeading: HTMLElement | null = reviewsContainer.querySelector(
      NO_REVIEWS_HEADING_SELECTOR
    );

    // If there are reviews, then use js to parse the reviews count string from the HTML
    // and then insert that total as a new div that contains the reviews count.
    // For example: <div class="ratings-total">(34)</div>
    if (reviewsText) {
      const reviewsTotal = parseInt(reviewsText);
      const starsDiv: HTMLElement | null = reviewsContainer.querySelector(
        '.tt-c-reviews-summary__stars'
      );
      const ratingsCountDiv = document.createElement('div');
      ratingsCountDiv.classList.add('ratings-total');
      ratingsCountDiv.innerText = `(${reviewsTotal})`;
      starsDiv?.appendChild(ratingsCountDiv);
    }

    // If this is a no reviews UI, then use CSS to insert a zero :before the stars
    // and a "(0)" :after the stars
    if (noNoReviewsHeading) {
      const starsContainer: HTMLElement | null = reviewsContainer.querySelector(
        '.tt-c-reviews-summary__no-reivews .tt-c-rating.tt-c-rating--actionable'
      );
      starsContainer?.classList.add('no-reviews');
    }
  },

  // insertCta() is a function that inserts a CTA button into the PDP Review
  // Summary widget. This was done so that when a user attempts to write a review, the
  // user is directed to a stand alone brand page with a header and footer that contains
  // the TurnTo write a review form instead of the default behavior, a modal. This CTA looks
  // like <a href="/write-a-review page?sku=[PRODUCT SKU]">. This is a P0 implementation.
  // Write A Review Form (warf) dependencies:
  //   sku          - product id
  //   turnToConfig - object that contains config values for TurnTo widgets
  //     Example of how this object is initialized:
  //       var turnToConfig = {
  //         locale: "en_US",
  //         pageId: "pdp-page",
  //         sku: "AEX201",
  //       };
  insertCta() {
    if (!reviewsContainer) {
      return;
    }
    // Only attempt inserting the CTA if Review Summary HTML exists
    const reviewSummaryContainer = reviewsContainer.querySelector(REVIEWS_SUMMARY_SELECTOR);
    const noReviewsContainer = reviewsContainer.querySelector(NO_REVIEWS_SUMMARY_SELECTOR);

    if (reviewSummaryContainer) {
      const ctaLink = this.createCTALink(window.turnToConfig.sku);
      reviewSummaryContainer?.appendChild(ctaLink);
    }

    if (noReviewsContainer) {
      const ctaLink = this.createCTALink(window.turnToConfig.sku);
      noReviewsContainer?.appendChild(ctaLink);
    }
  },

  // Callback function for reviewsList onUpdate event
  dispatchPDPReviewSort() {
    const reviewsSortVal: string = (
      reviewsContainer?.querySelector(REVIEWS_SORT_SELECTOR) as HTMLElement
    )?.innerText;
    if (reviewsSortVal) {
      const reviewSort = {
        sortVal: reviewsSortVal,
        prodID: window.turnToConfig.sku
      };

      window.dispatchEvent(new CustomEvent('pdp.onReviewSort', { detail: reviewSort }));
    }
  },
  showWidgetHeader() {
    const header = document.querySelector(REVIEWS_WIDGET_HEADER_SELECTOR);
    header?.classList.remove('hidden');
  },
  initTurnToConfigs() {
    const sku = reviewsContainer?.dataset.sku;

    window.turnToConfig = {
      locale: reviewsContainer?.dataset.locale,
      pageId: 'pdp-page',
      sku,
      reviewsList: {
        onUpdate: this.dispatchPDPReviewSort,
        onFinish: () => {
          setTimeout(() => {
            this.showWidgetHeader();
            this.insertReviewTotal();
            this.insertCta();
          }, WAIT_TIME);
        }
      }
    };

    window.TurnToCmd =
      window.TurnToCmd ||
      function (...args) {
        (TurnToCmd.q = TurnToCmd.q || []).push(args);
      };
  },
  initTurnToWriteAReviewConfigs() {
    const params = new URLSearchParams(document.location.search);
    const prodID = params.get('sku');

    function dispatchPDPReviewSubmit() {
      window.dispatchEvent(
        new CustomEvent('pdp.onReviewSubmit', {
          detail: { prodID: window?.turnToConfig?.sku ?? 0 }
        })
      );
    }

    const script = document.createElement('script');
    script.textContent = `
      var turnToConfig = {
        locale: '${reviewsContainer?.dataset.locale}',
        pageId: 'pdp-page-embedded-submit',
        sku: '${prodID}',
        eventHandlers: {
          onReviewSubmit: (${dispatchPDPReviewSubmit.toString()})
        }
      };
    `;
    document.body?.appendChild(script);

    window.TurnToCmd =
      window.TurnToCmd ||
      function (...args) {
        (TurnToCmd.q = TurnToCmd.q || []).push(args);
      };
  },
  appendWriteReviewCmd(submissionForm) {
    if (submissionForm) {
      const script = document.createElement('script');
      script.textContent = `
        TurnToCmd('writeReview')
      `;
      document.body?.appendChild(script);
    }
  },
  setDomainExt(locale: string) {
    switch (locale) {
      case 'en_GB':
        return 'eu';
      default:
        return 'com';
    }
  },
  loadTurnToScript() {
    if (!reviewsContainer) {
      return;
    }

    const siteKey = reviewsContainer.dataset.siteKey;
    const domainExt =
      reviewsContainer.dataset.domainExt || this.setDomainExt(reviewsContainer.dataset.locale);
    const src = `https://widgets.turnto.${domainExt}/v5/widgets/${siteKey}/js/turnto.js`;

    const scriptLoaded = Array.from(document.getElementsByTagName('script')).find(
      (script) => script.src === src
    );

    if (scriptLoaded) {
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    document.body?.appendChild(script);
  }
};
